import { PageHero } from "@/components/PageHero";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
    FileSignature, CheckCircle2, ShieldCheck, Clock, HeartHandshake,
    Sparkles, Video, Lock, CalendarCheck2,
} from "lucide-react";
import { toast } from "sonner";
import CalendlyInlineEmbed from "@/components/CalendlyInlineEmbed";

// One clean path: everyone starts with the FREE 15-minute walkthrough.
// The paid 30/60-minute sessions stay locked until the free call has
// happened (checked against real Calendly bookings via /api/booking/gate).

const DOC_OPTIONS = [
    { v: "living_will", label: "Living Will", desc: "Your wishes for end-of-life care, in writing." },
    { v: "mpoa", label: "Medical Power of Attorney (MPOA)", desc: "Names someone to make medical decisions if your loved one can't." },
    { v: "dpoa", label: "Durable Power of Attorney (DPOA)", desc: "Authorizes a trusted person to handle finances and legal matters." },
    { v: "dnr", label: "DNR / Out-of-Hospital DNR", desc: "Do-not-resuscitate orders, honored by EMS and hospitals." },
    { v: "polst", label: "POLST / MOST form", desc: "Doctor's orders for life-sustaining treatment near end of life." },
    { v: "guardianship", label: "Guardianship guidance", desc: "If your loved one can no longer sign documents themselves." },
    { v: "other", label: "Something else", desc: "Tell us in the notes below." },
];

const HELP_TOPICS = [
    "Safety planning", "Legal documents", "Family conversations", "Roadmap assistance",
    "Local resources", "Self-care planning", "Difficult decisions", "Goals of care",
];

const FALLBACK_STATES = [{ code: "TX", name: "Texas" }];
const UNLOCK_LS_KEY = "ml_paid_sessions_unlocked";

export default function Navigator() {
    const [form, setForm] = useState({
        name: "", email: "", phone: "", state: "TX",
        documents: [], preferred_time: "", notes: ""
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);
    const [states, setStates] = useState(FALLBACK_STATES);

    useEffect(() => {
        api.get("/resources/states").then(r => {
            setStates(r.data.filter(s => s.code !== "ALL"));
        }).catch(() => { });
    }, []);

    const toggleDoc = (v) => {
        setForm(f => ({
            ...f,
            documents: f.documents.includes(v)
                ? f.documents.filter(x => x !== v)
                : [...f.documents, v]
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Please share your name and email.");
            return;
        }
        if (!/^\S+@\S+\.\S+$/.test(form.email)) {
            toast.error("That email doesn't look right.");
            return;
        }
        if (form.documents.length === 0) {
            toast.error("Pick at least one area to discuss.");
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post("/navigator-request", form);
            setDone(r.data);
        } catch {
            toast.error("Couldn't reach the server. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="max-w-2xl mx-auto animate-fade-in-up">
                <div className="ml-card p-8 md:p-12 text-center bg-[#E6F4F4] !border-[#E6F4F4]" data-testid="navigator-success">
                    <CheckCircle2 size={48} strokeWidth={1.5} className="text-teal mx-auto mb-4" />
                    <h2 className="text-3xl md:text-4xl font-heading font-medium text-teal">
                        Request received.
                    </h2>
                    <p className="mt-3 text-lg text-[#1F2624] max-w-md mx-auto">
                        {done.message}
                    </p>
                    <p className="mt-3 text-sm text-[#4C5551]">
                        Ashlee gets an email right away and will reach out personally.
                    </p>
                    <button
                        onClick={() => { setDone(null); setForm({ name: "", email: "", phone: "", state: "TX", documents: [], preferred_time: "", notes: "" }); }}
                        data-testid="navigator-again"
                        className="ml-btn-secondary mt-7"
                    >Submit another request</button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fade-in-up">
            <PageHero
                testId="navigator-header"
                kicker="Care Navigator"
                kickerIcon={FileSignature}
                title="A real person, walking with you."
                subtitle={<>Live 1:1 video sessions with a trained dementia care navigator. Everyone starts with a free 15-minute walkthrough — we&apos;ll meet, hear what&apos;s on your plate, and name one clear next step. No card needed.</>}
            />

            {/* FREE 15-min walkthrough — the only booking path shown up front. */}
            <section
                id="book-free-walkthrough"
                data-testid="navigator-free-walkthrough"
                className="ml-card p-6 md:p-8 bg-gradient-to-br from-coral-light/60 via-cream-light to-teal-soft/30 !border-coral-light/70"
            >
                <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={18} strokeWidth={1.8} className="text-coral-dark" />
                    <p className="text-xs uppercase tracking-wider font-semibold text-coral-dark">
                        Start here · Free · 15 minutes
                    </p>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#12776F]">
                    Book your free 15-minute walkthrough.
                </h2>
                <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#4C5551]" data-testid="navigator-free-perks">
                    <li className="inline-flex items-center gap-1.5"><Clock size={14} strokeWidth={1.8} /> 15 minutes</li>
                    <li className="inline-flex items-center gap-1.5"><ShieldCheck size={14} strokeWidth={1.8} /> No card required</li>
                    <li className="inline-flex items-center gap-1.5"><Video size={14} strokeWidth={1.8} /> Calendly-secured video</li>
                    <li className="inline-flex items-center gap-1.5"><HeartHandshake size={14} strokeWidth={1.8} /> No commitment</li>
                </ul>
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="navigator-help-topics">
                    {HELP_TOPICS.map(t => (
                        <span key={t} className="rounded-2xl bg-white/80 border border-cream-dark px-2.5 py-1 text-xs text-[#4C5551]">{t}</span>
                    ))}
                </div>

                <div className="mt-5">
                    <CalendlyInlineEmbed
                        testid="navigator-free-calendly"
                        url={process.env.REACT_APP_BOOKING_URL || "https://calendly.com/ashlee-asvmedicalservices/new-meeting"}
                    />
                </div>
            </section>

            {/* Paid 30/60 sessions — locked until the free walkthrough happened. */}
            <GatedPaidSessions />

            {/* Callback request — for people not ready to pick a time. */}
            <section className="ml-card p-6 md:p-8 space-y-6" data-testid="navigator-form">
                <div>
                    <h2 className="text-2xl md:text-3xl font-heading font-medium text-teal">
                        Or — request a callback instead
                    </h2>
                    <p className="text-[#4C5551] mt-1.5">
                        Not ready to book? Tell us a little and Ashlee will email you within 2 business days.
                    </p>
                </div>

                <form onSubmit={submit} noValidate className="space-y-5">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="ml-label" htmlFor="nav-name">Your name *</label>
                            <input id="nav-name" data-testid="navigator-name" className="ml-input"
                                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                                placeholder="e.g. Maria L." required />
                        </div>
                        <div>
                            <label className="ml-label" htmlFor="nav-email">Email *</label>
                            <input id="nav-email" type="email" data-testid="navigator-email" className="ml-input"
                                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                                placeholder="you@example.com" required />
                        </div>
                        <div>
                            <label className="ml-label" htmlFor="nav-phone">Phone (optional)</label>
                            <input id="nav-phone" data-testid="navigator-phone" className="ml-input"
                                value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                                placeholder="(555) 555-5555" />
                        </div>
                        <div>
                            <label className="ml-label" htmlFor="nav-state">State you live in</label>
                            <select id="nav-state" data-testid="navigator-state" className="ml-input"
                                value={form.state} onChange={e => setForm({ ...form, state: e.target.value })}>
                                {states.map(s => (
                                    <option key={s.code} value={s.code}>{s.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="ml-label">Which documents or topics? *</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                            {DOC_OPTIONS.map(d => {
                                const checked = form.documents.includes(d.v);
                                return (
                                    <button
                                        type="button"
                                        key={d.v}
                                        data-testid={`navigator-doc-${d.v}`}
                                        onClick={() => toggleDoc(d.v)}
                                        className={`text-left p-4 rounded-xl border transition-colors ${checked
                                            ? "bg-[#E6F4F4] border-teal-light"
                                            : "bg-white border-[#E2E8F0] hover:bg-cream-dark"}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${checked
                                                ? "bg-teal border-teal"
                                                : "border-[#CBD5E0]"}`}>
                                                {checked && <CheckCircle2 size={14} className="text-white" strokeWidth={2.5} />}
                                            </div>
                                            <div>
                                                <div className="font-heading font-medium text-[#1F2624]">{d.label}</div>
                                                <div className="text-sm text-[#4C5551] mt-0.5">{d.desc}</div>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label className="ml-label" htmlFor="nav-time">When works for you?</label>
                        <input id="nav-time" data-testid="navigator-time" className="ml-input"
                            value={form.preferred_time}
                            onChange={e => setForm({ ...form, preferred_time: e.target.value })}
                            placeholder="e.g. Weekday evenings, Saturday mornings" />
                    </div>

                    <div>
                        <label className="ml-label" htmlFor="nav-notes">Anything else we should know?</label>
                        <textarea id="nav-notes" data-testid="navigator-notes"
                            className="ml-input min-h-[120px] py-3"
                            value={form.notes}
                            onChange={e => setForm({ ...form, notes: e.target.value })}
                            placeholder="Where you are in the journey, urgency, anything we should hold gently…" />
                    </div>

                    <p className="text-xs text-[#4C5551]">
                        Memory Lane Care Navigator helps you complete paperwork your state already provides.
                        We do not replace an attorney. For complex estates, please consult a licensed lawyer.
                    </p>

                    <div className="flex justify-end">
                        <button type="submit" disabled={submitting} data-testid="navigator-submit"
                            className="ml-btn-primary disabled:opacity-50">
                            {submitting ? "Sending…" : "Request my session"}
                        </button>
                    </div>
                </form>
            </section>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Paid sessions — gated behind the free 15-minute walkthrough         */
/* ------------------------------------------------------------------ */

const NAVIGATOR_SESSIONS = [
    {
        key: "30",
        label: "30-min consult",
        blurb: "A focused conversation about your most pressing concern.",
        url: "https://calendly.com/ashlee-asvmedicalservices/30minute-meeting",
    },
    {
        key: "60",
        label: "60-min deep-dive",
        blurb: "A longer session for big decisions — moving, hospice, family meetings.",
        url: "https://calendly.com/ashlee-asvmedicalservices/60min",
    },
];

function GatedPaidSessions() {
    const [unlocked, setUnlocked] = useState(
        () => typeof window !== "undefined" && localStorage.getItem(UNLOCK_LS_KEY) === "1"
    );
    const [scheduledFor, setScheduledFor] = useState(null);
    const [checkEmail, setCheckEmail] = useState("");
    const [checking, setChecking] = useState(false);

    const applyGate = (data) => {
        if (data?.unlocked) {
            setUnlocked(true);
            localStorage.setItem(UNLOCK_LS_KEY, "1");
        } else if (data?.scheduled_for) {
            setScheduledFor(data.scheduled_for);
        }
        return data?.unlocked || false;
    };

    // Auto-check with the logged-in account email.
    useEffect(() => {
        if (unlocked) return;
        api.get("/auth/me").then(r => {
            const email = r.data?.email || r.data?.user?.email;
            if (email) api.get(`/booking/gate/${encodeURIComponent(email)}`).then(g => applyGate(g.data)).catch(() => { });
        }).catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkByEmail = async () => {
        const em = checkEmail.trim().toLowerCase();
        if (!/^\S+@\S+\.\S+$/.test(em)) {
            toast.error("Enter the email you used to book your free call.");
            return;
        }
        setChecking(true);
        try {
            const g = await api.get(`/booking/gate/${encodeURIComponent(em)}`);
            const ok = applyGate(g.data);
            if (ok) toast.success("Welcome back — longer sessions are unlocked.");
            else if (g.data?.scheduled_for) toast.info("Your walkthrough is scheduled. Sessions unlock right after it happens.");
            else toast.error("We couldn't find a free walkthrough under that email yet.");
        } catch {
            toast.error("Couldn't check right now. Please try again.");
        } finally {
            setChecking(false);
        }
    };

    if (unlocked) {
        return (
            <section id="book-care-navigator" data-testid="calendly-inline-section" className="ml-card p-6 md:p-8 space-y-4">
                <div className="flex items-center gap-2">
                    <CalendarCheck2 size={18} strokeWidth={1.8} className="text-teal-dark" />
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">
                        Unlocked · Care navigator sessions
                    </p>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#12776F]">
                    Need more time?
                </h2>
                <p className="text-base text-[#4C5551] max-w-2xl leading-relaxed">
                    Book a longer one-to-one with the same nurse practitioner you already met.
                </p>
                <NavigatorScheduler />
            </section>
        );
    }

    return (
        <section
            id="book-care-navigator"
            data-testid="navigator-paid-locked"
            className="ml-card p-6 md:p-8 bg-cream-light"
        >
            <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-2xl bg-white border border-cream-dark text-[#6E7A74] flex items-center justify-center shrink-0">
                    <Lock size={17} strokeWidth={1.8} />
                </span>
                <div className="min-w-0">
                    <h2 className="font-heading text-2xl md:text-3xl font-medium text-[#12776F]">
                        30- and 60-minute sessions unlock after your free walkthrough.
                    </h2>
                    <p className="mt-1.5 text-sm md:text-base text-[#4C5551] max-w-2xl leading-relaxed">
                        {scheduledFor
                            ? "Your free walkthrough is on the calendar — longer sessions unlock automatically right after it happens."
                            : "We want the first 15 minutes to be free, so you can meet Ashlee and decide if a longer session is right for you. Book the walkthrough above — no card, no commitment."}
                    </p>
                    {scheduledFor && (
                        <p className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-teal-dark" data-testid="navigator-gate-scheduled">
                            <CalendarCheck2 size={13} strokeWidth={2} /> Walkthrough scheduled: {new Date(scheduledFor).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </p>
                    )}
                    <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="navigator-gate-check">
                        <p className="text-xs text-[#6E7A74] w-full">Already had your free call? Enter the email you booked with:</p>
                        <input
                            type="email"
                            value={checkEmail}
                            onChange={e => setCheckEmail(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") checkByEmail(); }}
                            placeholder="you@example.com"
                            data-testid="navigator-gate-email"
                            className="ml-input !min-h-[42px] !py-2 max-w-xs text-sm"
                        />
                        <button type="button" onClick={checkByEmail} disabled={checking}
                            data-testid="navigator-gate-submit"
                            className="ml-btn-secondary text-sm !min-h-[42px] !py-2 disabled:opacity-50">
                            {checking ? "Checking…" : "Unlock sessions"}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}

function NavigatorScheduler() {
    const [active, setActive] = useState("30");
    const current = NAVIGATOR_SESSIONS.find(s => s.key === active) || NAVIGATOR_SESSIONS[0];
    return (
        <div className="space-y-4">
            <div
                role="tablist"
                aria-label="Choose session length"
                className="flex flex-wrap gap-2"
                data-testid="navigator-session-toggle"
            >
                {NAVIGATOR_SESSIONS.map(s => (
                    <button
                        key={s.key}
                        type="button"
                        role="tab"
                        aria-selected={active === s.key}
                        data-testid={`navigator-session-${s.key}`}
                        onClick={() => setActive(s.key)}
                        className={`px-4 py-2.5 rounded-2xl text-sm font-medium transition-colors ${active === s.key
                            ? "bg-teal-dark text-white"
                            : "bg-white text-[#1F2624] border border-cream-dark hover:bg-cream-dark/30"
                            }`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>
            <p className="text-sm text-[#4C5551]" data-testid="navigator-session-blurb">
                {current.blurb}
            </p>
            <CalendlyInlineEmbed
                key={current.key}
                testid="navigator-calendly-inline"
                url={current.url}
            />
        </div>
    );
}
